import { create } from 'zustand';
import { adminContractGroupsApi } from '@api/api-config.ts';
import { AdminContractGroupListResponse, AdminContractGroupNpSyncResult } from '@api/generated/portal-be-admin';

export interface AdminContractGroupFilter {
  /** Část názvu skupiny. */
  q?: string;
  /** Název, zkratka nebo IČO firmy, která ve skupině je. */
  company?: string;
  page: number;
  size: number;
}

interface AdminContractGroupStoreState {
  loading: boolean;
  error: string | null;
  data: AdminContractGroupListResponse[] | null;

  pagination: {
    page: number;
    size: number;
    total: number;
    totalPages: number;
  } | null;

  /** Poslední použitý filtr — po uložení v dialogu se seznam načte znovu se stejným rozsahem. */
  filter: AdminContractGroupFilter;

  /** Běží synchronizace z NP. */
  syncing: boolean;
  /** Výsledek poslední synchronizace; `null` dokud se nespustí nebo po zavření panelu. */
  syncResult: AdminContractGroupNpSyncResult | null;

  loadContractGroups: (filter: AdminContractGroupFilter) => Promise<void>;
  reloadContractGroups: () => Promise<void>;
  syncFromNp: () => Promise<string | null>;
  clearSyncResult: () => void;
}

export const useAdminContractGroupStore = create<AdminContractGroupStoreState>((set, get) => ({
  loading: false,
  error: null,
  data: null,
  pagination: null,
  filter: { page: 1, size: 20 },
  syncing: false,
  syncResult: null,

  loadContractGroups: async (filter: AdminContractGroupFilter) => {
    try {
      set({ loading: true, error: null, filter });
      const response = await adminContractGroupsApi.getContractGroups({
        q: filter.q,
        organizationQuery: filter.company,
        page: filter.page,
        size: filter.size,
      });
      set({
        loading: false,
        error: null,
        data: response.data.items,
        pagination: {
          page: response.data.page,
          size: response.data.size,
          total: response.data.total || 0,
          totalPages: response.data.totalPages || 0,
        },
      });
    } catch (error) {
      set({ loading: false, error: error instanceof Error ? error.message : 'Chyba při načítání smluvních skupin' });
    }
  },

  reloadContractGroups: async () => {
    await get().loadContractGroups(get().filter);
  },

  /** Vrací chybovou hlášku, nebo `null` když synchronizace proběhla. */
  syncFromNp: async () => {
    try {
      set({ syncing: true, syncResult: null });
      const response = await adminContractGroupsApi.syncContractGroupsFromNp();
      set({ syncing: false, syncResult: response.data });
      await get().reloadContractGroups();
      return null;
    } catch (e: any) {
      set({ syncing: false });
      return e?.response?.data?.message || (e instanceof Error ? e.message : 'Synchronizace selhala');
    }
  },

  clearSyncResult: () => set({ syncResult: null }),
}));
